import { AsyncActionButton, Constants, EditWorksheetDto, FilteredCreditApplication, Loader, reportErrorToConsole, useFeatureFlags, useMerchantById, useUpdateWorksheet } from '@applyonline/shared'
import { EFinancingProgram, EPaymentFrequency, EPaymentPlan, EWorksheetStatus } from '@if/core/types'
import {
  formatCurrency,
  formatCurrencyNoDecimals,
  formatCurrencyRoundedUpToNoDecimals,
} from '@if/core/utils'
import { useComputeEstimatedPayment } from '../../../api/compute-api'
import { ManageCoappButton } from '../../../components'

import { FormEventHandler, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { getLoanAmount, hasFlexPromotion } from '../../../data/credit-application-selectors'
import { useTrackStepReachedEvent } from '../../../services/trackingEvents'
import { ETrackedEvents } from '../../../types/tracking'
import ApprovedSoftHitAndHardHitFooter from '../components/ApprovedSoftHitAndHardHitFooter'
interface Props {
  creditApp: FilteredCreditApplication
}

const InitialApprobationForm = ({ creditApp }: Props) => {
  const { t } = useTranslation()
  const [merchant, isLoadingMerchant] = useMerchantById(creditApp.merchantId, creditApp.financingProgramId)
  const [estimatedPaymentResult, isFetchingEstimatedPayment, isEstimatedPaymentError] = useComputeEstimatedPayment(creditApp.id, 'qual-result')
  const maxLoanTerm: number = estimatedPaymentResult?.term ?? 0

  const [submitWorksheetUpdate, isSubmitting] = useUpdateWorksheet(EFinancingProgram.Personal)
  useTrackStepReachedEvent(creditApp, ETrackedEvents.QualifSuccess)

  const [featureFlags, isFetchingFeatureFlags] = useFeatureFlags(EFinancingProgram.Personal)
  // Ticket #65379 - when the merged-consent flow is on, footnote 2 is no longer shown on this page.
  const useNewConsentFlow = featureFlags?.useNewApplyConsentFlow ?? false

  const hasAdminFee = (estimatedPaymentResult?.borrowerFeeRate ?? 0) > 0
  const exponent = hasAdminFee ? '1' : ''
  const hasFlex = hasFlexPromotion(estimatedPaymentResult)
  const promotionText = hasFlex ? 'decision.hardhitDecisionApprovedFlex' : 'decision.hardhitDecisionApprovedFlash'
  const hasCoapplicant = creditApp.coapplicant != null
  const hasChanceForPromotionalPlan =
    (merchant?.hasActivePlanPromoRules && creditApp.merchantPaymentPlan === null) ||
    (creditApp.prequalPromoCode != null && creditApp.merchantPaymentPlan === null)
  const canAddCoapp =
    creditApp.consentHardHit === true &&
    creditApp.initialRequestedLoanAmount > creditApp.finalDecision.maxAmountFinanced &&
    !Constants.MerchantsThatForbidHavingCoapplicants.includes(creditApp.merchantId)

  const submitTerm: FormEventHandler = (e) => {
    e.preventDefault()
    if (creditApp) {
      const dto: EditWorksheetDto = {
        term: estimatedPaymentResult?.term ?? 0,
        amountRequested: getLoanAmount(creditApp),
        creditApplicationId: creditApp.id,
        paymentFrequency: EPaymentFrequency.Monthly,
        deliveryOn: null,
        firstPaymentOn: null,
        includeInsurance: false,
        status: EWorksheetStatus.Draft,
        paymentPlanId: creditApp.merchantPaymentPlanId || EPaymentPlan.RegularDailyInterests,
        create: creditApp.worksheet === null,
        versionTag: creditApp.worksheet?.versionTag ?? null,
      }
      submitWorksheetUpdate(dto).catch(reportErrorToConsole)
    }
  }
  const getTitle = useMemo<string>(() => {
    const hasSkipIncomeVerification =
      creditApp.applicant.hasSkippedIncomeValidation &&
      (creditApp.coapplicant != null ? creditApp.coapplicant.hasSkippedIncomeValidation : true)
    return hasSkipIncomeVerification
      ? t('decision.hardhitDecisionApprovedTitle')
      : t('decision.hardhitDecisionConditionallyApprovedTitle')
  }, [creditApp, t])

  if (isFetchingEstimatedPayment || isEstimatedPaymentError || !creditApp || isLoadingMerchant || isFetchingFeatureFlags)
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />

  const requestedAmountReached = creditApp.requestedLoanAmount <= creditApp.finalDecision.maxAmountFinanced
  const loanAmount = Math.min(creditApp.requestedLoanAmount, creditApp.finalDecision.maxAmountFinanced)

  return (
    <main className="general-message" style={{ display: 'block' }}>
      <form onSubmit={submitTerm}>
        <div id="loan-form-container">
          <h3>
            {requestedAmountReached && (
              <>
                <i className="fa-solid fa-party-horn" /> {t(useNewConsentFlow ? 'common.greatNews' : 'common.congratulation')}{' '}
              </>
            )}
            {getTitle}
          </h3>
          <div className="paragraph">
            {estimatedPaymentResult?.isPromotional && (
              <p>
                <Trans
                  i18nKey={promotionText}
                  values={{
                    maximumAmount: formatCurrencyNoDecimals(creditApp.finalDecision.maxAmountFinanced),
                    reducedInterestRate: estimatedPaymentResult.interestRate,
                    reducedRateDurationInMonths: estimatedPaymentResult.promoDurationMonths ?? estimatedPaymentResult.term,
                  }}
                />
                !{hasFlex && <sup style={{ fontSize: '1rem', color: 'red' }}>1</sup>}
                &nbsp;
                <Trans
                  i18nKey="decision.estimatedPayment"
                  values={{
                    monthlyPayment: formatCurrency(estimatedPaymentResult.monthlyPayment),
                  }}
                />
                .{hasFlex && hasAdminFee && <sup style={{ fontSize: '1rem', color: 'red' }}>2</sup>}
                {!hasFlex && exponent && hasAdminFee && <sup style={{ fontSize: '1rem', color: 'red' }}>1</sup>}
              </p>
            )}

            {estimatedPaymentResult?.isPromotional === false && (
                <>
                  <p style={{ textAlign: useNewConsentFlow ? 'left' : 'center', ...(useNewConsentFlow ? { lineHeight: '1.6' } : {}) }}>
                    <Trans
                      i18nKey="decision.hardhitDecisionApproved"
                      values={{
                        maximumAmount: formatCurrencyNoDecimals(creditApp.finalDecision.maxAmountFinanced),
                        minPaymentAmount: formatCurrencyNoDecimals(estimatedPaymentResult.monthlyPayment),
                      }}
                    />
                  </p>
                  {canAddCoapp && !hasChanceForPromotionalPlan && (
                    <div style={{ marginTop: '32px', marginBottom: '32px' }}>
                      <div className="strong">{t('prequalification.addCoapplicant.title')}</div>
                      <div className="btn-group">
                        <ManageCoappButton creditAppId={creditApp.id} useChangeLabel={hasCoapplicant} merchantId={creditApp.merchantId} isHardDecline={creditApp.finalDecision.isHardDecline}/>
                      </div>
                    </div>
                  )}
                  {hasChanceForPromotionalPlan && <div className="strong">{t('common.regularPlanQualified')}</div>}
                </>
              )}
          </div>
          <div>
            <div className="btn-group mobile-reversed justify-end" style={{ marginTop: '32px' }}>
              {hasChanceForPromotionalPlan && (
                <ManageCoappButton creditAppId={creditApp.id} useChangeLabel={hasCoapplicant} merchantId={creditApp.merchantId} isHardDecline={creditApp.finalDecision.isHardDecline}/>
              )}
              <AsyncActionButton type="submit" className="btn btn-blue" isPending={isSubmitting} primary>
                <span>
                  {requestedAmountReached
                    ? t(useNewConsentFlow ? 'common.completeApplication' : 'common.imInterested')
                    : t('common.continueWith', { amount: formatCurrencyRoundedUpToNoDecimals(loanAmount) })}
                </span>
                <i className="fa-regular fa-arrow-right" />
              </AsyncActionButton>
            </div>
            <div className="paragraph">
              <p style={{ textAlign: 'center' }}>
                <br />
                <Trans
                  i18nKey="loan.youHaveQuestions"
                  values={{
                    phoneNumber: '1-888-689-9876',
                  }}
                  components={{ a: <Link to="tel:18886899876" style={{ textWrap: 'nowrap' }} /> }}
                />
              </p>
            </div>
          </div>
          <div style={{ marginTop: '4rem' }}>
            <ApprovedSoftHitAndHardHitFooter
              estimatedPayment={estimatedPaymentResult}
              minInterestRate={estimatedPaymentResult?.interestRate ?? creditApp.finalDecision.interestRate}
              hasCoapplicant={hasCoapplicant}
              maxLoanTerm={maxLoanTerm}
              useNewConsentFlow={useNewConsentFlow}
              displayFooter2={false}
            />
          </div>
        </div>
      </form>
    </main>
  )
}

export default InitialApprobationForm
